
import { Box, Button, Card, CardContent, Divider, Grid, IconButton, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { IconDownload, IconPlus, IconX } from "@tabler/icons-react";
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { Content, TDocumentDefinitions } from "pdfmake/interfaces";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { convertImageToBase64 } from "../../Utils/generatePDF";
import { getStores } from "../../api/storeApi";

(pdfMake.vfs as any) = pdfFonts.vfs;

const SupplyBilling: React.FC = () => {
    const logo = './letter_head_final.png';
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [invoice, setInvoice] = useState<any>();
    const token = useSelector((state: any) => state.user.sessionId);
    const [storeList, setStoreList] = useState<any[]>([]);

    useEffect(() => {
        getStores().then((data) => {
            setStoreList(data);
        }).catch((error) => { console.log(error) })
    }, []);

    const formattedNumber = (num: number): string => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
        }).format(num);
    };

    const generatePdf = async (download: boolean) => {
        // copy the invoice no to clipboard
        if (invoice?.invoice_no) {
            navigator.clipboard.writeText(invoice.invoice_no);
        }
        const imageDataUrl = await convertImageToBase64(logo);
        const noOfRows = invoice?.products?.length < 10 ? 10 : invoice?.products?.length;
        const subTotal = invoice?.products?.reduce((total: number, product: any) => total + product.unit_price * product.quantity, 0) || 0;
        const hst = subTotal * 0.13 || 0;
        // format the subTotal and hst to 2 decimal places and comma separated
        const formattedSubTotal = formattedNumber(subTotal);
        const formattedHST = formattedNumber(hst);
        const total = formattedNumber(subTotal + hst);
        // const noOfRows = 10;
        let content: any[] = [
            {
                columns: [
                    {
                        image: imageDataUrl,
                        alignment: 'left',
                        margin: [-20, -30, 0, 0],
                        fit: [110, 200], // Add this property to specify the image size
                        style: 'redBackground'
                    }, {
                        // text: `SVP SPORTS ${invoice?.dc_location?.location || ''} \n ${invoice?.dc_location?.address || ''} \n ${invoice?.dc_location?.city || ''} ${invoice?.dc_location?.postal_code || ''} \n ${invoice?.dc_location?.contact[0]?.phone || ''}`,
                        table: {
                            widths: [200],
                            body: [
                                [{ text: `2138398 Ontario Inc.`, fontSize: 16, bold: true, color: '#04162e' }],
                                [{ text: `${invoice?.dc_location?.address || ''}`, fontSize: 12, margin: [0, -8, 0, 0], color: '#04162e' }],
                                [{ text: `${invoice?.dc_location?.city || ''} ${invoice?.dc_location?.postal_code || ''}`, fontSize: 12, margin: [0, -7, 0, 0], color: '#04162e' }],
                                [{ text: `${invoice?.dc_location?.contact ? invoice?.dc_location?.contact[0]?.phone || '' : ''}`, fontSize: 12, margin: [0, -7, 0, 0], color: '#04162e' }],
                            ],
                        }, layout: {
                            defaultBorder: false,
                        },
                        margin: [-160, -30, 0, 0],
                    }]
            }, {
                table: {
                    widths: [585, 0],
                    // heights: [30],
                    body: [
                        [
                            {
                                text: `INVOICE`,
                                alignment: 'right',
                                fontSize: 17,
                                // background: '#000000',
                                color: '#ffffff',
                                bold: true,
                                margin: [10, 10, 0, 0],
                                fillColor: '#000000', // ✅ use fillColor instead of background
                            },
                        ]
                    ],
                },
                layout: {
                    defaultBorder: false,
                },
                margin: [-40, 20, 0, 10],
            }, {
                columns: [
                    {
                        table: {
                            widths: [70, 230],
                            body: [
                                [{ text: `BILL TO: `, fontSize: 16, bold: true, color: '#04162e' }, { text: `${invoice?.bill_to?.corporate_no || ''}`, fontSize: 16, bold: true, color: '#04162e' }],
                                [{}, { text: `SVP SPORTS ${invoice?.bill_to?.location || ''}`, bold: true, fontSize: 12, margin: [0, -5, 0, 0], color: '#04162e' }],
                                [{}, { text: `${invoice?.bill_to?.address || ''}`, fontSize: 12, margin: [0, -5, 0, 0], color: '#04162e' }],
                                [{}, { text: `${invoice?.bill_to?.city || ''} ${invoice?.bill_to?.postal_code || ''}`, fontSize: 12, margin: [0, -5, 0, 0], color: '#04162e' }],
                                [{}, { text: `${invoice?.bill_to?.contact ? invoice?.bill_to?.contact[0]?.phone || '' : ''}`, fontSize: 12, margin: [0, -5, 0, 0], color: '#04162e' }],
                            ],
                        }, layout: {
                            defaultBorder: false,
                        },
                        margin: [-30, 0, 0, 0],
                    },
                    {
                        table: {
                            widths: [75, 230],
                            body: [
                                [{ text: `Invoice#: `, fontSize: 16, bold: true, color: '#04162e', fillColor: '#E0E0E0' }, { text: `${invoice?.invoice_no || ''}`, fontSize: 16, bold: true, color: '#04162e', fillColor: '#E0E0E0' }],
                                [{}, {}],
                                [{}, {}],
                                [{ text: `Date       : `, fontSize: 16, color: '#04162e', fillColor: '#E0E0E0' }, { text: `${invoice?.date || ''}`, fontSize: 16, color: '#04162e', fillColor: '#E0E0E0' }],
                            ],
                        }, layout: {
                            defaultBorder: false,
                        },
                        margin: [-25, 0, 0, 0],
                    },
                ]
            }, {
                text: `Note: ${invoice?.instructions || 'N/A'}`,
                fontSize: 12,
                margin: [-20, 0, 0, 0],
            }, {
                table: {
                    headerRows: 1,
                    widths: [30, 290, 40, 70, 120],
                    heights: function (i: number) {
                        return i === 0 ? 25 : 20; // First row height is 30, others are 20
                    },
                    body: [
                        [
                            { text: 'No.', style: 'title' },
                            { text: 'Description', style: 'title', alignment: 'left' },
                            { text: 'QTY', style: 'title' },
                            { text: 'Unit price', style: 'title' },
                            { text: 'Total', style: 'title' }
                        ],
                        ...Array(noOfRows).fill(null).map((_, index) => [
                            { text: `${invoice?.products[index]?.description ? (index + 1) : ''}`, style: 'data', border: [false, false, false, true], },
                            { text: `${invoice?.products[index]?.description || ''}`, style: 'data', alignment: 'left', border: [false, false, false, true] },
                            { text: `${invoice?.products[index]?.quantity || ''}`, style: 'data', border: [false, false, false, true] },
                            { text: `${invoice?.products[index]?.unit_price ? formattedNumber(invoice?.products[index]?.unit_price) : ''}`, style: 'data', border: [false, false, false, true] },
                            { text: `${(invoice?.products[index]?.unit_price && invoice?.products[index]?.quantity) ? formattedNumber(invoice?.products[index]?.unit_price * invoice?.products[index]?.quantity) : ''}`, style: 'data', border: [false, false, false, true] },
                        ]),
                    ]
                }, margin: [-40, 10, 0, 0], alignment: 'center',
                layout: {
                    defaultBorder: false,
                    // highlight alternatingrows
                    hLineWidth: (i: number) => (i === 0 ? 2 : 1),
                    vLineWidth: () => 0.8,
                    hLineColor: () => '#9e9e9e',
                    vLineColor: () => '#0416',
                    paddingBottom: (i: number) => (5),
                }
            },
            {
                columns: [{
                    table: {
                        widths: [230, 70],
                        body: [
                            [{ text: `HST No: 858397359 RT0001`, fontSize: 16, color: '#04162e' }, { text: ``, fontSize: 16, color: '#04162e' }],],
                    }, layout: {
                        defaultBorder: false,
                    }
                }, {
                    table: {
                        widths: [123, 230],
                        body: [
                            [{ text: `Subtotal: `, fontSize: 13, color: '#04162e', border: [false, false, false, true] }, { text: `${formattedSubTotal || ''}`, fontSize: 13, color: '#04162e', margin: [0, 0, 0, 0], border: [false, false, false, true] }],
                            [{ text: `HST(13%): `, fontSize: 13, color: '#04162e', border: [false, false, false, true] }, { text: `${formattedHST || ''}`, fontSize: 13, color: '#04162e', margin: [0, 0, 0, 0], border: [false, false, false, true] }],
                            [{ text: `Total amount due: `, fontSize: 13, bold: true, color: '#04162e', fillColor: '#E0E0E0' }, { text: `${total || ''}`, fontSize: 13, bold: true, color: '#04162e', fillColor: '#E0E0E0', margin: [0, 0, 0, 0] }],
                        ],
                    }, layout: {
                        defaultBorder: false,
                    },
                    margin: [-20, 0, 0, 0],
                },
                ]
            },
        ]
        const docDefinition: TDocumentDefinitions = {
            content: content,
            // put a table as footer
            footer: (currentPage: number, pageCount: number) => {
                return {
                    table: {
                        widths: [600],
                        heights: [10],
                        body: [
                            [
                                {
                                    text: `THANK YOU FOR YOUR BUSINESS!`,
                                    alignment: 'center',
                                    fontSize: 8,
                                    // background: '#000000',
                                    color: '#ffffff',
                                    bold: true,
                                    margin: [0, 0, 0, 0],
                                    fillColor: '#000000', // ✅ use fillColor instead of background
                                },
                            ]
                        ],
                    },
                    layout: {
                        defaultBorder: false,
                    },
                    margin: [-10, 13, 0, 10],

                }
            },
            styles: {
                tabe: {
                },
                header: {
                    fillColor: '#04162e',
                    color: 'white',
                    fontSize: 10,
                    bold: true,
                    alignment: 'center'
                },
                body: {
                    color: 'black',
                    fontSize: 15,
                    alignment: 'center'
                },
                title: {
                    fillColor: '#000000',
                    color: '#ffffff',
                    fontSize: 14,
                    bold: true,
                    alignment: 'center'
                },
                data: {
                    fontSize: 12,
                    bold: true,
                    alignment: 'center'
                }
            },
        };

        // Generate PDF as Blob URL
        pdfMake.createPdf(docDefinition).getBlob((blob) => {
            const url = URL.createObjectURL(blob);
            if (iframeRef.current) {
                iframeRef.current.src = url;
            }
        });
        // if download is true download the pdf
        if (download) {
            pdfMake.createPdf(docDefinition).download(`invoice_${invoice?.invoice_no || 'unknown'}.pdf`);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setInvoice((prev: any) => ({
            ...prev,
            [name]: value
        }));

    };

    const handleSelectChange = (name: string, value: string) => {
        if (name === 'dc_location' || name === 'bill_to' || name === 'ship_to') {
            // get the store info
            const selectedStore = storeList.find(store => store.name === value);
            if (selectedStore) {
                setInvoice((prev: any) => ({
                    ...prev,
                    [name]: selectedStore
                }))
            }
        }
    }
    const handleProductChange = (column: string, value: string, index: number) => {
        setInvoice((prev: any) => {
            const updatedProducts = [...(prev.products || [])];
            updatedProducts[index][column] = value;
            return {
                ...prev,
                products: updatedProducts
            };
        });
    }

    const handleAddProduct = () => {
        //if no products exist, initialize it
        if (!invoice?.products) {
            setInvoice((prev: any) => ({
                ...prev,
                products: [{ label: '', description: '', skid: 0 }]
            }));
            return;
        } else {
            setInvoice((prev: any) => ({
                ...prev,
                products: [...(prev.products || []), { label: '', description: '', skid: 0 }]
            }));
        }
    }

    const handleRemoveProduct = (index: number) => {
        setInvoice((prev: any) => {
            const updatedProducts = [...(prev.products || [])];
            updatedProducts.splice(index, 1);
            return {
                ...prev,
                products: updatedProducts
            };
        });
    }
    console.log('Invoice::  ', invoice);

    return (
        <Box sx={{ p: { xs: 1.5, md: 3 } }}>
            <Stack spacing={2.5}>
                <Box>
                    <Typography variant="h4" fontWeight={800}>
                        Supply Billing
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Create invoice details on the left and preview the generated PDF on the right.
                    </Typography>
                </Box>

                <Grid container spacing={2}>
                    {/* Left: Form Section */}
                    <Grid size={{ xs: 12, lg: 5 }}>
                        <Card
                            sx={{
                                borderRadius: 3,
                                border: '1px solid',
                                borderColor: 'divider',
                                boxShadow: 'none',
                                height: '100%',
                            }}
                        >
                            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                                <Stack spacing={2.5}>m
                                    <Typography variant="h6" fontWeight={700}>
                                        Invoice Details
                                    </Typography>

                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                                select
                                                fullWidth
                                                size="small"
                                                label="DC Location"
                                                value={invoice?.dc_location?.name || ''}
                                                onChange={(e) =>
                                                    handleSelectChange('dc_location', e.target.value || '')
                                                }
                                            >
                                                {storeList.map((store) => (
                                                    <MenuItem key={store.name} value={store.name}>
                                                        {store.location}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>

                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                                select
                                                fullWidth
                                                size="small"
                                                label="Bill To"
                                                value={invoice?.bill_to?.name || ''}
                                                onChange={(e) =>
                                                    handleSelectChange('bill_to', e.target.value || '')
                                                }
                                            >
                                                {storeList.map((store) => (
                                                    <MenuItem key={store.name} value={store.name}>
                                                        {store.location}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>

                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Invoice No"
                                                name="invoice_no"
                                                value={invoice?.invoice_no || ''}
                                                onChange={handleInputChange}
                                                placeholder="Enter invoice no..."
                                            />
                                        </Grid>

                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Date"
                                                name="date"
                                                type="date"
                                                value={invoice?.date || ''}
                                                onChange={handleInputChange}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Grid>

                                        <Grid size={12}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Instructions"
                                                name="instructions"
                                                value={invoice?.instructions || ''}
                                                onChange={handleInputChange}
                                                placeholder="Enter instructions..."
                                            />
                                        </Grid>
                                    </Grid>

                                    <Divider />

                                    {/* Products */}
                                    <Stack spacing={1.5}>
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                        >
                                            <Typography variant="h6" fontWeight={700}>
                                                Products
                                            </Typography>

                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<IconPlus size={16} />}
                                                onClick={handleAddProduct}
                                            >
                                                Add Product
                                            </Button>
                                        </Stack>

                                        <TableContainer
                                            component={Paper}
                                            variant="outlined"
                                            sx={{
                                                borderRadius: 2,
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, width: 120 }}>
                                                            Quantity
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 700, width: 140 }}>
                                                            Unit Price
                                                        </TableCell>
                                                        <TableCell sx={{ width: 70, fontWeight: 700 }}>
                                                            Action
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>

                                                <TableBody>
                                                    {invoice?.products?.length ? (
                                                        invoice.products.map((product: any, index: number) => (
                                                            <TableRow key={index}>
                                                                <TableCell>
                                                                    <TextField
                                                                        fullWidth
                                                                        size="small"
                                                                        placeholder="Description"
                                                                        value={product.description || ''}
                                                                        onChange={(e) =>
                                                                            handleProductChange(
                                                                                'description',
                                                                                e.target.value,
                                                                                index
                                                                            )
                                                                        }
                                                                    />
                                                                </TableCell>

                                                                <TableCell>
                                                                    <TextField
                                                                        fullWidth
                                                                        size="small"
                                                                        type="number"
                                                                        placeholder="Qty"
                                                                        value={product.quantity || ''}
                                                                        onChange={(e) =>
                                                                            handleProductChange(
                                                                                'quantity',
                                                                                e.target.value,
                                                                                index
                                                                            )
                                                                        }
                                                                    />
                                                                </TableCell>

                                                                <TableCell>
                                                                    <TextField
                                                                        fullWidth
                                                                        size="small"
                                                                        placeholder="Unit Price"
                                                                        value={product.unit_price || ''}
                                                                        onChange={(e) =>
                                                                            handleProductChange(
                                                                                'unit_price',
                                                                                e.target.value,
                                                                                index
                                                                            )
                                                                        }
                                                                    />
                                                                </TableCell>

                                                                <TableCell align="center">
                                                                    <IconButton
                                                                        color="error"
                                                                        onClick={() => handleRemoveProduct(index)}
                                                                    >
                                                                        <IconX size={16} />
                                                                    </IconButton>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))
                                                    ) : (
                                                        <TableRow>
                                                            <TableCell colSpan={4} align="center">
                                                                <Typography variant="body2" color="text.secondary">
                                                                    No products added.
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Stack>

                                    <Divider />

                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            onClick={() => generatePdf(false)}
                                        >
                                            Preview PDF
                                        </Button>

                                        <Button
                                            fullWidth
                                            variant="contained"
                                            startIcon={<IconDownload size={16} />}
                                            onClick={() => generatePdf(true)}
                                        >
                                            Download PDF
                                        </Button>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Right: Preview Section */}
                    <Grid size={{ xs: 12, lg: 7 }}>
                        <Card
                            sx={{
                                borderRadius: 3,
                                border: '1px solid',
                                borderColor: 'divider',
                                boxShadow: 'none',
                                height: '100%',
                            }}
                        >
                            <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                                <Stack spacing={1.5}>
                                    <Box>
                                        <Typography variant="h6" fontWeight={700}>
                                            PDF Preview
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Review the invoice output before downloading.
                                        </Typography>
                                    </Box>

                                    <Box
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            height: { xs: '65vh', md: '78vh' },
                                            backgroundColor: '#fff',
                                        }}
                                    >
                                        <iframe
                                            ref={iframeRef}
                                            title="PDF Preview"
                                            width="100%"
                                            height="100%"
                                            style={{ border: 'none' }}
                                        />
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Stack>
        </Box>
    );
};

export default SupplyBilling;